import { DeliverLast } from '@hypatia/streams/operators/deliver-last'
import { useRef, useState } from 'react'
import { useContext } from 'use-context-selector'
import { SoftSuspenseContext } from './SoftSuspense'
import { useEffectRightNow } from './useEffectRightNow'
import { Result } from './useStreamOrPromise'

export interface StreamConfig<V, E, C> {
    throwIfError?: boolean
    suspense?: boolean
    onValue?: (value: V) => void
    onError?: (error: E) => void
    onComplete?: (complete: C) => void
}

export default function useDeliverLastOld<V, E, C>(
    stream: DeliverLast<V, E, C> | undefined,
    { suspense = false, throwIfError = false, onComplete, onError, onValue } = {} as StreamConfig<V, E, C>
) {
    const suspenseFn = useContext(SoftSuspenseContext)
    const [, /* state */ setThrowableError] = useState()

    // eslint-disable-next-line prefer-const
    let [results, setResults] = useState<Result<V, E, C>>(
        stream?.connect && stream.received
            ? {
                  loading: false,
                  status: 'running',
                  value: stream.last!,
                  complete: undefined,
                  error: undefined,
              }
            : {
                  loading: true,
                  status: 'loading',
              }
    )

    const promiseRef = useRef<{ promise: Promise<any>; resolve: () => void } | null>()

    // for suspending only
    if (suspense && results.status === 'loading' && (!stream || !stream.received)) {
        if (!stream) {
            if (!promiseRef.current) {
                const obj: { promise: Promise<any>; resolve: () => void } = {} as any
                const promise = new Promise((resolve) => {
                    obj.resolve = resolve
                })
                obj.promise = promise
                promiseRef.current = obj

                // calling suspense too early is causing react to warn against a child updating its parent state
                setTimeout(() => {
                    suspenseFn(promiseRef.current!.promise)
                }, 0)
            }
        } else {
            const promise = stream.getLast()
            if (promiseRef.current) {
                promise.then(() => promiseRef.current!.resolve())
            } else {
                // calling suspense too early is causing react to warn against a child updating its parent state
                setTimeout(() => {
                    suspenseFn(promise)
                }, 0)
            }
        }
    }

    useEffectRightNow(() => {
        if (!stream) {
            return
        }

        return stream.subscribe({
            value: (value) => {
                onValue?.(value)

                results = {
                    ...results,
                    loading: false,
                    status: 'running',
                    value: value,
                } as Result<V, E, C>

                setResults(
                    (currentResults) =>
                        ({
                            ...currentResults,
                            loading: false,
                            status: 'running',
                            value: value,
                        } as Result<V, E, C>)
                )
            },
            error: (error) => {
                if (throwIfError) {
                    setThrowableError(() => {
                        throw error
                    })
                }
                onError?.(error)
                setResults(
                    (currentResults) =>
                        ({
                            ...currentResults,
                            loading: false,
                            status: 'error',
                            error,
                        } as Result<V, E, C>)
                )
            },
            complete: (complete) => {
                onComplete?.(complete)
                setResults(
                    (currentResults) =>
                        ({
                            ...currentResults,
                            loading: false,
                            status: 'completed',
                            complete,
                        } as Result<V, E, C>)
                )
            },
        })

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [stream])

    return results
}
